import axios from 'axios';
import { useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useInterval } from 'react-use';
import { RootState } from 'store/root-reducer';
import { AppConfig } from '../config/config.model';
import { setIsUsingV2Endpoints } from 'redux/global-elements-slice';

const POLL_RATE = 300000; // 5m in ms
const MAX_FAIL_RETRIES = 3;

/**
 * Hook that retrieves COTA feature flags from the API every 5 minutes.
 * If the endpoint fails 3 times then unsubscribes.
 */
const useFeatureFlags = (): void => {
    const config = useSelector((state: RootState) => state.globalElements.appConfig as AppConfig);
    const dispatch = useDispatch();

    const [isSetup, setIsSetup] = useState(false);
    const [delay, setDelay] = useState<number | null>(POLL_RATE);
    const [failRetryCount, setFailRetryCount] = useState(0);

    const fetchFlags = useCallback(() => {
        if (!config?.TIMESHEET_SERVICE_BASE_URL) return;
        axios
            .get(`${config.TIMESHEET_SERVICE_BASE_URL}/Configuration/FeatureFlags`)
            .then((response) => {
                dispatch(setIsUsingV2Endpoints(response.data?.['COTA.IsUsingV2Endpoints'] ?? false));
                setFailRetryCount(0);
            })
            .catch(() => {
                if (failRetryCount + 1 >= MAX_FAIL_RETRIES) {
                    setDelay(null);
                }
                setFailRetryCount((state) => state + 1);
            });
    }, [config?.TIMESHEET_SERVICE_BASE_URL, dispatch, failRetryCount]);

    useEffect(() => {
        if (config?.TIMESHEET_SERVICE_BASE_URL && !isSetup) {
            setIsSetup(true);
            fetchFlags();
        }
    }, [config?.TIMESHEET_SERVICE_BASE_URL, fetchFlags, isSetup]);

    useInterval(() => fetchFlags(), delay);
};

export default useFeatureFlags;
